import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean indicating whether the ref'd element
 * is within `rootMargin` of the viewport.  Once true, stays true
 * (one-way latch) so the component doesn't unload when scrolled away.
 *
 * Falls back to `isNear: true` when IntersectionObserver is not supported.
 */
export function useNearViewport<T extends HTMLElement = HTMLDivElement>(
  rootMargin = '400px'
) {
  const ref = useRef<T>(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    if (isNear) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsNear(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsNear(true);
          observer.disconnect(); // one-way: never disable again
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, isNear]);

  return { ref, isNear };
}
